import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { key: 1 }
const _hoisted_2 = { class: "col" }
const _hoisted_3 = { class: "col span-6" }
const _hoisted_4 = { class: "col span-8" }
const _hoisted_5 = {
  key: 0,
  class: "col span-8"
}
const _hoisted_6 = {
  key: 1,
  class: "col span-6"
}
const _hoisted_7 = { class: "col span-8" }

import { ref, watch, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import NameNsDescription from '@shell/components/form/NameNsDescription.vue';
import LabeledInput from '@components/Form/LabeledInput/LabeledInput.vue';
import KeyValue from '@shell/components/form/KeyValue.vue';
import ArrayList from '@shell/components/form/ArrayList.vue';
import Loading from '@shell/components/Loading.vue';
import Banner from '@components/Banner/Banner.vue';
import ChartValues from '../settings/ChartValues.vue';
import { _EDIT } from '@shell/config/query-params';

import { sortBy } from '@shell/utils/sort';
import { validateKubernetesName } from '@shell/utils/validators/kubernetes-name';
import { EPINIO_TYPES, EpinioNamespace, EpinioAppInfo } from '../../types';
import Application from '../../models/applications';
import { objValuesToString } from '../../utils/settings';

/*interface Data {
  errors: string[],
  values?: EpinioAppInfo
}*/


export default /*@__PURE__*/_defineComponent({
  __name: 'AppInfo',
  props: {
    application: {},
    mode: {}
  },
  emits: ["valid", "change"],
  setup(__props: any, { emit: __emit }) {

const store = useStore();

const t = store.getters['i18n/t'];

// Props
const props = __props;

// Emit function
const emit = __emit;

// Reactive state
const errors = ref<string[]>([]); // eslint-disable-line @typescript-eslint/no-unused-vars
const values = ref<EpinioAppInfo | undefined>(undefined);
const validSettings = ref<{ [key: string]: boolean }>({});


// Computed properties
const namespaces = computed(() => {
  return sortBy(store.getters['epinio/all'](EPINIO_TYPES.NAMESPACE), 'name', false);
});

const namespaceNames = computed(() => namespaces.value.map((n: EpinioNamespace) => n.meta?.name));

const valid = computed(() => {
  if (!values.value) {
    return false;
  }
  const validName = !!values.value.meta?.name;

  const nsErrors = validateKubernetesName(
    values.value.meta?.namespace || '',
    '',
    store.getters,
    undefined,
    [],
  );
  const validNamespace = nsErrors.length === 0;
  const validInstances = typeof Number(values.value.configuration?.instances) !== 'string' && 
    values.value.configuration?.instances >= 0;
  
  return validName && validNamespace && validInstances && 
    Object.values(validSettings.value).every((v) => !!v);
});

const showApplicationVariables = computed(() => {
  return Object.keys(values.value?.configuration?.settings || {}).length !== 0;
});

const isEdit = computed(() => props.mode === _EDIT);

// Mounted lifecycle hook
onMounted(() => {
  const valuesData: EpinioAppInfo = {
    meta: {
      name: props.application.meta?.name,
      namespace: props.application.meta?.namespace || namespaces.value[0]?.meta?.name
    },
    chart: moveBooleansToFront(props.application.chart?.settings) || {},
    configuration: {
      configurations: props.application.configuration?.configurations || [],
      instances: props.application.configuration?.instances || 1,
      environment: props.application.configuration?.environment || {},
      settings: props.application.configuration?.settings || {},
      routes: props.application.configuration?.routes || [],
    },
  };

  values.value = valuesData;
  validSettings.value = {};

  emit('valid', valid.value);

  populateOnEdit();
});

// Methods
const update = () => {
  emit('change', {
    meta: values.value?.meta,
    configuration: {
      ...values.value?.configuration,
      settings: objValuesToString(values.value?.configuration.settings)
    },
  });
};

// Watchers
watch(() => values.value?.configuration.instances, (newVal) => {
  values.value.configuration.instances = Number(newVal);
  update()
});
watch(() => values.value?.configuration.environment, update);
watch(() => values.value?.configuration.settings, update, { deep: true });
watch(() => values.value?.configuration.routes, update);
watch(valid, (newValid) => {
  emit('valid', newValid);
});

const populateOnEdit = async () => {
  // We need to fetch the chart settings on edit mode.
  if (isEdit.value || props.mode === 'view') {
    const chartList = await store.dispatch(
      'epinio/findAll', 
      { type: EPINIO_TYPES.APP_CHARTS },
    );

    const filterChart = chartList?.find(
      (chart: any) => chart.id === props.application.configuration.appchart
    );

    if (filterChart?.settings) {
      const customValues = Object.keys(filterChart?.settings).reduce((acc: any, key: any) => {
        acc[key] = props.application.configuration.settings[key] || '';
        return acc;
      }, {});

      values.value.configuration.settings = customValues;
      values.value.chart = moveBooleansToFront(filterChart.settings);
    }
  }
};

// Allows us to move the checkbox at the top of the list so layout-wise looks better
const moveBooleansToFront = (settingsObj: any) => {
  if (!settingsObj) {
    return;
  }
  const entries = Object.entries(settingsObj);

  entries.sort((a: any, b: any) => {
    const aValue = a[1].type === 'bool' ? 0 : 1;
    const bValue = b[1].type === 'bool' ? 0 : 1;

    return aValue - bValue;
  });

  return Object.fromEntries(entries);
};

return (_ctx: any,_cache: any) => {
  return (!values.value)
    ? (_openBlock(), _createBlock(Loading, { key: 0 }))
    : (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(NameNsDescription, {
            "data-testid": "epinio_app-info_name-ns",
            "name-key": "name",
            "namespace-key": "namespace",
            "namespaces-override": namespaceNames.value,
            "create-namespace-override": true,
            "description-hidden": true,
            value: values.value.meta,
            mode: props.mode,
            onChange: update,
            onCreateNamespace: _cache[0] || (_cache[0] = ns => values.value.meta.namespace = ns)
          }, null, 8, ["namespaces-override", "value", "mode"])
        ]),
        _cache[10] || (_cache[10] = _createTextVNode()),
        _createElementVNode("div", _hoisted_3, [
          _createVNode(LabeledInput, {
            value: values.value.configuration.instances,
            "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((values.value.configuration.instances) = $event)),
            "data-testid": "epinio_app-info_instances",
            type: "number",
            min: "0",
            required: "",
            mode: props.mode,
            label: _unref(t)('epinio.applications.create.instances')
          }, null, 8, ["value", "mode", "label"])
        ]),
        _cache[11] || (_cache[11] = _createTextVNode()),
        _cache[12] || (_cache[12] = _createElementVNode("div", { class: "spacer" }, null, -1)),
        _cache[13] || (_cache[13] = _createTextVNode()),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(ArrayList, {
            value: values.value.configuration.routes,
            "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((values.value.configuration.routes) = $event)),
            "data-testid": "epinio_app-info_routes",
            title: _unref(t)('epinio.applications.create.routes.title'),
            protip: _unref(t)('epinio.applications.create.routes.tooltip'),
            mode: props.mode,
            "value-placeholder": _unref(t)('epinio.applications.create.routes.placeholder')
          }, null, 8, ["value", "title", "protip", "mode", "value-placeholder"])
        ]),
        _cache[14] || (_cache[14] = _createTextVNode()),
        _cache[15] || (_cache[15] = _createElementVNode("div", { class: "spacer" }, null, -1)),
        _cache[16] || (_cache[16] = _createTextVNode()),
        (isEdit.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createVNode(Banner, { color: "info" }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('epinio.applications.create.settingsVars.description')), 1)
                ]),
                _: 1
              })
            ]))
          : _createCommentVNode("", true),
        _cache[17] || (_cache[17] = _createTextVNode()),
        (showApplicationVariables.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createVNode(ChartValues, {
                value: values.value.configuration.settings,
                "onUpdate:value": _cache[3] || (_cache[3] = ($event: any) => ((values.value.configuration.settings) = $event)),
                chart: values.value.chart,
                title: _unref(t)('epinio.applications.create.settingsVars.title'),
                mode: props.mode,
                disabled: false,
                onValid: _cache[4] || (_cache[4] = ($event: any) => (validSettings.value = $event))
              }, null, 8, ["value", "chart", "title", "mode"]),
              _cache[6] || (_cache[6] = _createTextVNode()),
              _cache[7] || (_cache[7] = _createElementVNode("div", { class: "spacer" }, null, -1))
            ]))
          : _createCommentVNode("", true),
        _cache[18] || (_cache[18] = _createTextVNode()),
        _createElementVNode("div", _hoisted_7, [
          _createVNode(KeyValue, {
            value: values.value.configuration.environment,
            "onUpdate:value": _cache[5] || (_cache[5] = ($event: any) => ((values.value.configuration.environment) = $event)),
            "data-testid": "epinio_app-info_envs",
            mode: props.mode,
            title: _unref(t)('epinio.applications.create.envvar.title'),
            "key-label": _unref(t)('epinio.applications.create.envvar.keyLabel'),
            "value-label": _unref(t)('epinio.applications.create.envvar.valueLabel'),
            "parse-lines-from-file": true
          }, null, 8, ["value", "mode", "title", "key-label", "value-label"]),
          _cache[8] || (_cache[8] = _createTextVNode()),
          _cache[9] || (_cache[9] = _createElementVNode("div", { class: "mb-20" }, null, -1))
        ])
      ]))
}
}

})